// Canonical catalog of publicly orderable, automated document services.
// The SERVER treats this file as the single source of truth for pricing —
// clients can never submit their own price. Every order created from this
// catalog is paid via PayFast.

export type DocumentServiceField = {
  name: string;
  label: string;
  type: "text" | "textarea" | "select";
  required?: boolean;
  placeholder?: string;
  options?: string[];
  help?: string;
};

export type DocumentService = {
  id: string;
  name: string;
  category:
    | "Contracts & agreements"
    | "Review & advice"
    | "Business documents"
    | "Employment & labour";
  tagline: string;
  priceZar: number;
  turnaround: string;
  bestFor: string;
  includes: string[];
  fields: DocumentServiceField[];
};

const COMMON_DETAIL: DocumentServiceField = {
  name: "details",
  label: "Tell us what the document must cover",
  type: "textarea",
  required: true,
  placeholder:
    "Parties, amounts, dates, obligations, deadlines and anything that must appear in the document.",
  help: "The more detail you give, the more precise the first draft will be.",
};

export const DOCUMENT_SERVICES: DocumentService[] = [
  {
    id: "quick-contract",
    name: "Quick Precise Contract",
    category: "Contracts & agreements",
    tagline: "A tight, plain-language contract drafted around your facts.",
    priceZar: 450,
    turnaround: "24 hours",
    bestFor: "Once-off deals that need to be in writing today",
    includes: [
      "Bespoke contract drafted from your instructions",
      "Clause-numbered, signature-ready Word + PDF",
      "Plain-language note explaining each key clause",
      "One round of revisions included",
    ],
    fields: [
      {
        name: "contractType",
        label: "Type of contract",
        type: "text",
        required: true,
        placeholder: "e.g. Service agreement, loan agreement, sale of goods",
      },
      { name: "partyA", label: "Party A (you)", type: "text", required: true },
      { name: "partyB", label: "Party B (other side)", type: "text", required: true },
      { name: "value", label: "Contract value / fee", type: "text", placeholder: "R —" },
      COMMON_DETAIL,
    ],
  },
  {
    id: "agreement-pack",
    name: "Standard Agreement Pack",
    category: "Contracts & agreements",
    tagline: "Three linked agreements drafted together for one business.",
    priceZar: 1250,
    turnaround: "3 working days",
    bestFor: "Start-ups and small businesses setting up properly",
    includes: [
      "Three agreements of your choice (e.g. service, NDA, independent contractor)",
      "Consistent definitions and payment terms across all three",
      "POPIA clause included in each agreement",
      "Word + PDF, signature-ready, one revision round",
    ],
    fields: [
      {
        name: "agreements",
        label: "Which three agreements do you need?",
        type: "textarea",
        required: true,
        placeholder: "1. Service agreement\n2. NDA\n3. Independent contractor agreement",
      },
      { name: "business", label: "Business name", type: "text", required: true },
      COMMON_DETAIL,
    ],
  },
  {
    id: "contract-review",
    name: "Contract Review & Risk Report",
    category: "Review & advice",
    tagline: "We read the contract you were sent and tell you exactly where the risk sits.",
    priceZar: 650,
    turnaround: "48 hours",
    bestFor: "Anyone asked to sign something they don't fully understand",
    includes: [
      "Clause-by-clause review of up to 20 pages",
      "Risk register: red / amber / green with reasons",
      "Suggested replacement wording for problem clauses",
      "Written summary in plain English",
    ],
    fields: [
      {
        name: "documentLink",
        label: "Link to the document (Drive / Dropbox / WeTransfer)",
        type: "text",
        help: "Or reply to the confirmation email with the file attached.",
      },
      {
        name: "concern",
        label: "What worries you most about it?",
        type: "textarea",
        required: true,
      },
    ],
  },
  {
    id: "legal-opinion",
    name: "Written Opinion (Non-Reserved Matter)",
    category: "Review & advice",
    tagline: "A structured written opinion on your legal question.",
    priceZar: 950,
    turnaround: "5 working days",
    bestFor: "Managers, boards and business owners who need a documented position",
    includes: [
      "Issue, applicable law, application and recommendation",
      "Up to 12 pages with statutory and case references",
      "Practical next steps and risk flags",
      "Signed PDF + editable Word",
    ],
    fields: [
      {
        name: "question",
        label: "Your legal question",
        type: "textarea",
        required: true,
        placeholder: "State the question you want answered in one or two sentences.",
      },
      { name: "background", label: "Background facts", type: "textarea", required: true },
    ],
  },
  {
    id: "business-proposal",
    name: "Business / Tender Proposal",
    category: "Business documents",
    tagline: "A persuasive, correctly structured proposal ready to submit.",
    priceZar: 1450,
    turnaround: "4 working days",
    bestFor: "Bids, tenders and formal client proposals",
    includes: [
      "Executive summary, scope, methodology, pricing and timelines",
      "Compliance checklist against the tender requirements",
      "Company profile section formatted to match",
      "Designed PDF + editable Word",
    ],
    fields: [
      { name: "client", label: "Who is the proposal for?", type: "text", required: true },
      { name: "scope", label: "What are you proposing to deliver?", type: "textarea", required: true },
      { name: "deadline", label: "Submission deadline", type: "text", placeholder: "YYYY-MM-DD" },
      COMMON_DETAIL,
    ],
  },
  {
    id: "policy-drafting",
    name: "Company Policy Drafting",
    category: "Business documents",
    tagline: "One workplace or compliance policy, drafted to South African law.",
    priceZar: 750,
    turnaround: "3 working days",
    bestFor: "POPIA, disciplinary, leave, IT and social media policies",
    includes: [
      "Policy drafted against the relevant Act and codes",
      "Implementation note and employee acknowledgement form",
      "Word + PDF",
      "One revision round",
    ],
    fields: [
      { name: "policyType", label: "Which policy?", type: "text", required: true },
      { name: "headcount", label: "Approximate number of employees", type: "text" },
      COMMON_DETAIL,
    ],
  },
  {
    id: "letter-of-demand",
    name: "Letter of Demand",
    category: "Business documents",
    tagline: "A correctly formulated demand that gets taken seriously.",
    priceZar: 400,
    turnaround: "24 hours",
    bestFor: "Unpaid invoices, breaches and outstanding debts",
    includes: [
      "Letter of demand citing the agreement, amount and interest",
      "Prescription and NCA position checked",
      "Payment-arrangement annexure",
      "PDF ready to send, plus editable Word",
    ],
    fields: [
      { name: "debtor", label: "Who owes you?", type: "text", required: true },
      { name: "amount", label: "Amount outstanding", type: "text", required: true, placeholder: "R —" },
      COMMON_DETAIL,
    ],
  },
  {
    id: "employment-contract",
    name: "Employment Contract",
    category: "Employment & labour",
    tagline: "BCEA-compliant contract of employment for one role.",
    priceZar: 550,
    turnaround: "48 hours",
    bestFor: "Employers hiring permanent, fixed-term or part-time staff",
    includes: [
      "Contract compliant with the BCEA and LRA",
      "Restraint, confidentiality and POPIA clauses",
      "Job description annexure",
      "Word + PDF, signature-ready",
    ],
    fields: [
      { name: "role", label: "Job title", type: "text", required: true },
      {
        name: "employmentType",
        label: "Employment type",
        type: "select",
        required: true,
        options: ["Permanent", "Fixed-term", "Part-time", "Independent contractor"],
      },
      { name: "salary", label: "Remuneration", type: "text", placeholder: "R — per month" },
      COMMON_DETAIL,
    ],
  },
  {
    id: "disciplinary-pack",
    name: "Disciplinary Documentation Pack",
    category: "Employment & labour",
    tagline: "Every document you need to run a fair hearing.",
    priceZar: 1100,
    turnaround: "48 hours",
    bestFor: "Employers facing a misconduct or incapacity hearing",
    includes: [
      "Notice of hearing and charge sheet",
      "Evidence bundle index and witness statement templates",
      "Chairperson's ruling template",
      "Outcome and sanction letters",
    ],
    fields: [
      { name: "employee", label: "Employee (initials are fine)", type: "text", required: true },
      { name: "allegations", label: "What is the alleged misconduct?", type: "textarea", required: true },
      { name: "hearingDate", label: "Hearing date (if set)", type: "text", placeholder: "YYYY-MM-DD" },
    ],
  },
];

export const DOCUMENT_SERVICE_MAP: Record<string, DocumentService> =
  Object.fromEntries(DOCUMENT_SERVICES.map((s) => [s.id, s]));

export function getDocumentService(id: string): DocumentService | null {
  return DOCUMENT_SERVICE_MAP[id] ?? null;
}

export const DOCUMENT_SERVICE_CATEGORIES = Array.from(
  new Set(DOCUMENT_SERVICES.map((s) => s.category)),
);
